import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button, Code, Heading, Stack, Text,
} from '@chakra-ui/react';
import { isAxiosError } from 'axios';
import { useMemo } from 'react';
import { When } from 'react-if';

interface IErrorPage {
  error?: Error;
}

export default function ErrorPage({ error }: IErrorPage): React.ReactElement {
  const cleanedError = useMemo(() => {
    if (!error) return null;

    if (isAxiosError(error)) {
      return {
        title: error.code || 'Unknown error',
        detail: error.message,
        url: error.config?.url,
        data: error.response?.data,
        status: error.code,
      };
    }

    if (error instanceof Error) {
      return {
        title: error.name || 'Unknown error',
        detail: error.message,
        stack: error.stack?.split('\n'),
        status: error.code,
      };
    }

    return {
      title: 'Unknown error',
      detail: JSON.stringify(error),
    };
  }, [error]);

  return (
    <Stack
      spacing={10} direction='column'
      align='center'
    >
      <When condition={!!cleanedError}>
        <Heading as='h1' size='lg'>
          {cleanedError?.status ? `${cleanedError?.status} - ` : ''}{cleanedError?.title ?? 'Something went wrong'}
        </Heading>
        <Text>{cleanedError?.detail || 'Unable to fetch error details.'}</Text>
        <Accordion
          allowToggle w='70%'
          minW='400px'
        >
          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box
                  as='span' flex='1'
                  textAlign='left'
                >
                  Error Details
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <Code whiteSpace='pre-wrap'>{JSON.stringify(cleanedError, null, 2)}</Code>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </When>
      <Button
        width={150} size='sm'
        onClick={() => window.location.reload()}
      >
        Reload
      </Button>
    </Stack>
  );
}
